import {ProcedureMaterialInputSetting, ProcedureMaterialOutputSetting} from 'procedures/ProcedureDomainTypes';
import {
  extractMethodNodeFromProcessNode,
  getFieldClientId,
  ProcedureFieldGroupKey,
} from 'procedures/processes/MethodNodeUtils';
import {
  DataFlowEdgeForValidation,
  ProcessFlowchartForValidation,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {getFieldInfoFromDataFlowEdgeHandle} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {ProcessFlowchartEdgeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {FlowchartNodeChangeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {FlowchartChanges} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';

/**
 * Check if a field exists in a method node's applicationData (materialInputSettings or materialOutputSettings).
 * This is specific to procedure flowcharts where parameters are stored in the method's applicationData.
 * Returns null if the node does not exist or is not a method node.
 */
function doesFieldExistInMethodApplicationData(
  edge: DataFlowEdgeForValidation,
  flowchart: ProcessFlowchartForValidation,
  isSource: boolean
): boolean | null {
  const nodeId = isSource ? edge.source : edge.target;
  const handleId = isSource ? edge.sourceHandle : edge.targetHandle;

  if (!handleId) {
    return false;
  }

  const node = flowchart.nodes.find((n) => n.id === nodeId);
  if (!node) {
    return null;
  }

  const methodNode = extractMethodNodeFromProcessNode(node);
  if (!methodNode) {
    return null;
  }

  const fieldInfo = getFieldInfoFromDataFlowEdgeHandle(handleId);
  const fieldClientId = fieldInfo.fieldClientId;

  const existsInInputs = methodNode.materialInputSettings.some(
    (setting: ProcedureMaterialInputSetting) =>
      getFieldClientId(ProcedureFieldGroupKey.MATERIAL_INPUTS, setting.materialInputVersion.name) ===
      fieldClientId
  );
  const existsInOutputs = methodNode.materialOutputSettings.some(
    (setting: ProcedureMaterialOutputSetting) =>
      getFieldClientId(ProcedureFieldGroupKey.MATERIAL_OUTPUTS, setting.materialOutputVersion.name) ===
      fieldClientId
  );

  return existsInInputs || existsInOutputs;
}

/**
 * Check if a data flow edge references deleted procedure parameters in the method's applicationData.
 */
function doesProcedureDataFlowEdgeHaveDeletedParameters(
  edge: DataFlowEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): boolean {
  const sourceExists = doesFieldExistInMethodApplicationData(edge, flowchart, true);
  const targetExists = doesFieldExistInMethodApplicationData(edge, flowchart, false);
  return (sourceExists != null && !sourceExists) || (targetExists != null && !targetExists);
}

/**
 * Returns edge IDs for data flow edges that reference deleted procedure parameters.
 */
export function getEdgesWithDeletedProcedureParameters(
  changes: FlowchartChanges,
  validationFlowchart: ProcessFlowchartForValidation
): Array<string> {
  // Only check edges connected to nodes that were updated
  const updatedNodeIds = new Set<string>();
  for (const change of changes.nodeChanges) {
    if (change.type === FlowchartNodeChangeType.REPLACE) {
      updatedNodeIds.add(change.id);
    }
  }

  if (updatedNodeIds.size === 0) {
    return [];
  }

  const edgesToRemove: Array<string> = [];

  for (const edge of validationFlowchart.edges) {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
      continue;
    }

    if (!updatedNodeIds.has(edge.source) && !updatedNodeIds.has(edge.target)) {
      continue;
    }

    if (doesProcedureDataFlowEdgeHaveDeletedParameters(edge, validationFlowchart)) {
      edgesToRemove.push(edge.id);
    }
  }

  return edgesToRemove;
}
